import type { ToolCall } from '@kanbu/schema';
import { ToolType } from '@kanbu/schema/enums';
import type { StreamStateStatus } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import {
  Check,
  ChevronDown,
  Layers,
  Loader2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { motion } from 'motion/react';
import {
  type ReactNode,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BuiltinToolDetail } from './builtin-tool-detail';
import { ShopToolDetail, useToolResultSummary } from './shop-tool-detail';
import { toolActivityIcon, useToolPhrase } from './tool-activity-meta';
import { cn } from '../../../lib/cn';

export interface ActivityTrailProps {
  toolCalls: ToolCall[];
  status?: StreamStateStatus;
  hasAnswerContent: boolean;
  /**
   * Keep the trail expanded by default (per-step detail visible during the run
   * and after the answer streams). Off by default: a single calm line the user
   * can expand on demand. Admin-set via `chatbotConfig.expandToolActivity`.
   */
  defaultExpanded?: boolean;
  /**
   * Optional extra content rendered under each expanded step — the admin inbox
   * uses it to attach the raw call/result inspector to every trail entry when
   * debug is on. Unused (undefined) on the customer surface.
   */
  renderStepInspector?: (toolCall: ToolCall) => ReactNode;
}

type TrailToolCall = ToolCall & { type: ToolType.Tool };

function isTurnInFlight(status: StreamStateStatus | undefined): boolean {
  return status === 'start' || status === 'streaming';
}

/**
 * A call is running whenever it has produced no result yet and the turn is
 * still in flight — independent of its position, so several parallel searches
 * all show a spinner instead of only the last one. A stale `pending` after
 * the turn settles must not keep spinning (orphan / unmatched finish).
 */
function isToolRunning(
  toolCall: ToolCall,
  status: StreamStateStatus | undefined,
): boolean {
  if (toolCall.status === 'completed' || toolCall.status === 'error') {
    return false;
  }

  if (!isTurnInFlight(status)) {
    return false;
  }

  if (toolCall.status === 'pending') {
    return true;
  }

  return !toolCall.content;
}

/**
 * Per-tool detail shown under a settled step: compact shop facets, then the
 * built-in retrieval facets (query / URL). Each renderer returns `null` for
 * tools it does not own, so unknown / MCP tools render just their label line.
 */
function StepDetail({ toolCall }: { toolCall: TrailToolCall }) {
  return (
    <>
      <ShopToolDetail toolCall={toolCall} />
      <BuiltinToolDetail toolCall={toolCall} />
    </>
  );
}

function ActivityStep({
  toolCall,
  status,
  renderStepInspector,
}: {
  toolCall: TrailToolCall;
  status: StreamStateStatus | undefined;
  renderStepInspector?: (toolCall: ToolCall) => ReactNode;
}) {
  const toolPhrase = useToolPhrase();
  const toolResultSummary = useToolResultSummary();
  const running = isToolRunning(toolCall, status);
  const errored = toolCall.status === 'error';
  const Icon = toolActivityIcon(toolCall.name);
  const phrase = toolPhrase(toolCall);
  const resultSummary = running ? null : toolResultSummary(toolCall);

  return (
    <motion.div
      className='flex flex-col gap-1 py-1'
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className='flex items-center gap-2 text-xs text-chat-muted-foreground'>
        <Icon
          className={cn(
            'size-3.5 shrink-0 opacity-70',
            errored && 'text-destructive opacity-100',
          )}
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            errored && 'text-destructive',
          )}
        >
          {running ? phrase.running : phrase.past}
        </span>
        {resultSummary ? (
          <span className='shrink-0 font-medium text-chat-foreground'>
            {resultSummary}
          </span>
        ) : null}
        {running ? (
          <Loader2 className='size-3 shrink-0 animate-spin' />
        ) : errored ? (
          <X className='size-3 shrink-0 text-destructive' />
        ) : (
          <Check className='size-3 shrink-0 opacity-60' />
        )}
      </div>

      {/*
       * A failed call still gets a line — it is what happened — but no
       * detail: its "result" is an error string, and the per-tool detail
       * renderers would try to parse it as a result payload.
       */}
      {!running && !errored && <StepDetail toolCall={toolCall} />}

      {renderStepInspector ? (
        <div className='pl-6'>{renderStepInspector(toolCall)}</div>
      ) : null}
    </motion.div>
  );
}

/**
 * Unified, progressively-disclosed activity trail for every tool type on both
 * agent surfaces. Collapsed it is a single calm line whose verb mutates in
 * place as tools run (Searching → Reading → Comparing); expanded it shows the
 * accumulated per-tool detail. When the answer streams it folds to the last
 * completed task, with failures called out in the summary.
 */
export const ActivityTrail = memo(
  ({
    toolCalls,
    status,
    hasAnswerContent,
    defaultExpanded = false,
    renderStepInspector,
  }: ActivityTrailProps) => {
    const { t } = useLingui();
    const toolPhrase = useToolPhrase();

    const trailCalls = useMemo(
      () =>
        toolCalls.filter(
          (call): call is TrailToolCall => call.type === ToolType.Tool,
        ),
      [toolCalls],
    );

    /**
     * Only `start` / `streaming` mean the turn is still live. Persisted
     * history rows have no stream `status` — treating anything other than
     * `end`/`error` as active left the header spinning forever on reload.
     */
    const showSummary = hasAnswerContent || !isTurnInFlight(status);

    const [expanded, setExpanded] = useState(defaultExpanded);
    const userToggled = useRef(false);

    /**
     * Follow the configured default until the user takes over: `defaultExpanded`
     * keeps the trail open (during the run and after the answer streams);
     * otherwise it stays a single collapsed line the user can expand on demand.
     */
    useEffect(() => {
      if (userToggled.current) {
        return;
      }

      setExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const toggle = () => {
      userToggled.current = true;
      setExpanded(prev => !prev);
    };

    const lastCall = trailCalls.at(-1);
    const runningCall = isTurnInFlight(status)
      ? trailCalls.findLast(call => isToolRunning(call, status))
      : undefined;
    const failedCount = trailCalls.filter(
      call => call.status === 'error',
    ).length;

    const headerLabel = useMemo(() => {
      if (!lastCall) {
        return '';
      }

      if (expanded) {
        return t`Details`;
      }

      if (!showSummary && runningCall) {
        return toolPhrase(runningCall).running;
      }

      /**
       * A failure stays visible once the trail folds — otherwise the only
       * trace of a tool that never returned is behind a click.
       */
      if (failedCount > 0) {
        return t`Used ${trailCalls.length} tools · ${failedCount} failed`;
      }

      return toolPhrase(lastCall).past;
    }, [
      expanded,
      showSummary,
      trailCalls,
      lastCall,
      runningCall,
      failedCount,
      t,
      toolPhrase,
    ]);

    if (trailCalls.length === 0 || !lastCall) {
      return null;
    }

    const HeaderIcon = expanded
      ? Layers
      : showSummary
        ? failedCount > 0
          ? TriangleAlert
          : Check
        : toolActivityIcon((runningCall ?? lastCall).name);

    return (
      <motion.div
        className='mb-2 w-full max-w-full rounded-2xl bg-chat-surface/50'
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          type='button'
          aria-expanded={expanded}
          onClick={toggle}
          className={cn(
            'flex w-full items-center gap-2 rounded-2xl px-3 py-1.5 text-left',
            'text-xs text-chat-muted-foreground transition-colors hover:bg-chat-surface',
          )}
        >
          <HeaderIcon
            className={cn(
              'size-3.5 shrink-0',
              showSummary ? 'opacity-60' : 'opacity-70',
              showSummary && failedCount > 0 && 'text-destructive opacity-100',
            )}
          />
          <span className='min-w-0 flex-1 truncate'>{headerLabel}</span>
          {!expanded && runningCall && !showSummary && (
            <Loader2 className='size-3 shrink-0 animate-spin opacity-70' />
          )}
          <ChevronDown
            className={cn(
              'size-3 shrink-0 opacity-70 transition-transform duration-200 ease-out',
              !expanded && '-rotate-90',
            )}
          />
        </button>

        <div
          aria-hidden={!expanded}
          inert={!expanded}
          className={cn(
            'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
            expanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0',
          )}
        >
          <div className='min-h-0 overflow-hidden'>
            <div className='px-3 pb-1.5'>
              {trailCalls.map(call => (
                <ActivityStep
                  key={call.id}
                  toolCall={call}
                  status={status}
                  renderStepInspector={renderStepInspector}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  },
);
